// Customer-owned SSH keys — the shape a registered key has, the dialect its host
// patterns are written in, and the rule that picks one for a host a repo declared.
//
// WHY A CREDENTIAL CLASS OF ITS OWN. `git` already carries an SSH key, but that one
// is REPO-scoped and exists to clone/push OUR maintained repo. These keys reach the
// CUSTOMER's own infrastructure — a bastion, a database host, a deploy target — are
// ACCOUNT/USER-scoped (the same ownership split `byok_keys` uses: `account_id` NOT
// NULL, nullable `user_id`, NULL meaning the org pool), and one key is deliberately
// reusable across every repo the account maintains.
//
// THE SELECTION INPUT IS A COMMITTED FILE, NEVER A MODEL. Which host a run may dial
// is declared in the repo's `.maintainer.yml` and matched against each key's
// `host_patterns` here. The task envelope does not carry it, the agent cannot author
// it, and "whatever host the process happened to dial" is not an input to this
// function. That is the whole security property of the feature: a key is chosen by a
// reviewable file in the customer's own repo, by code that runs before the agent
// starts.
//
// WHAT SEALING DOES NOT BUY YOU. An SSH private key must exist as a FILE for `ssh(1)`
// to use it, so the model-driven agent running on that box can read it. `sealed` means
// "not written into the workspace and scrubbed from recorded output" — it has never
// meant "invisible to the agent" (see `CredentialExposure` in `project-ingress.ts`).
// The mitigation is on the customer's side: a deploy key, `from=` and `command=`
// restrictions in their `authorized_keys`. Nothing here promises otherwise.

import { z } from 'zod';

// ─── host patterns: the ssh_config `Host` dialect ────────────────────────────

/**
 * Bounds on an untrusted host pattern before it is compiled to a regex. Same
 * reasoning as `glob.ts`: `*` compiles to `.*`, and a pattern chaining many of them
 * backtracks catastrophically on a synchronous match nothing can abort. A
 * pathological pattern is REFUSED at the write seam by {@link SshHostPatternSchema}
 * and compiles to a never-match here, so neither layer can be the only guard.
 */
export const SSH_HOST_PATTERN_MAX = 253;
export const SSH_HOST_PATTERN_MAX_WILDCARDS = 8;
/** Patterns per key, and pinned host keys per key — a registration, not a corpus. */
export const SSH_HOST_PATTERNS_MAX = 32;
export const SSH_KNOWN_HOSTS_MAX = 32;
/** Longest key name; account-unique, shown in a picker, named in refusals. */
export const SSH_KEY_NAME_MAX = 64;

/**
 * One `host_patterns` entry, in **ssh_config `Host` grammar** — not the path-glob
 * dialect `glob.ts` compiles, and the difference is not cosmetic:
 *
 * | | `glob.ts` (paths) | here (hosts) |
 * |---|---|---|
 * | `*` | one path SEGMENT (`[^/]*`) | any run of characters, `.` included |
 * | `**` | slash-crossing | nothing special — two wildcards |
 * | case | sensitive | INSENSITIVE, as ssh compares hostnames |
 * | `!` | — | negates the entry (see {@link sshHostPatternsMatch}) |
 *
 * `db*.internal` therefore matches `db-3.eu.internal`, which is what an operator who
 * has written an `ssh_config` expects and what the segment-aware dialect would refuse.
 * Reading a host pattern with the path dialect would silently narrow every customer's
 * pattern, so the two live side by side rather than one being reused for both.
 *
 * A COMMA IS REFUSED rather than split. ssh's own file takes `Host a,b` as a list;
 * this column is already an array, so a comma inside one entry is a pattern that will
 * never match anything — the failure this feature can least afford to be quiet about.
 */
export const SshHostPatternSchema = z
  .string()
  .min(1)
  .max(SSH_HOST_PATTERN_MAX)
  .refine((p) => !p.includes(','), {
    message: 'one pattern per entry — a comma-separated list belongs in separate entries',
  })
  .refine((p) => /^!?[A-Za-z0-9._\-*?[\]:]+$/.test(p), {
    message:
      'a host pattern holds host characters plus the ssh wildcards `*` and `?`, optionally negated with a leading `!`',
  })
  .refine((p) => (p.match(/[*?]/g) ?? []).length <= SSH_HOST_PATTERN_MAX_WILDCARDS, {
    message: `at most ${SSH_HOST_PATTERN_MAX_WILDCARDS} wildcards in one pattern`,
  })
  .refine((p) => p.replace(/^!/, '').length > 0, { message: 'a bare `!` negates nothing' });
export type SshHostPattern = z.infer<typeof SshHostPatternSchema>;

/**
 * A CONCRETE host a run may dial — what `.maintainer.yml` declares and what a
 * pattern is matched against. No wildcards: a declaration carrying one would let the
 * repo widen its own reach to every key the account holds, which is the inversion
 * this feature exists to prevent.
 */
export const SshHostSchema = z
  .string()
  .min(1)
  .max(SSH_HOST_PATTERN_MAX)
  .regex(/^[A-Za-z0-9._\-[\]:]+$/, 'a host is a hostname or an IP literal, never a pattern');

/** Compile one positive ssh_config host pattern to an anchored, case-folded RegExp. */
function hostPatternToRegExp(pattern: string): RegExp {
  if (
    pattern.length > SSH_HOST_PATTERN_MAX ||
    (pattern.match(/[*?]/g) ?? []).length > SSH_HOST_PATTERN_MAX_WILDCARDS
  ) {
    return /(?!)/;
  }
  let re = '';
  for (const c of pattern) {
    if (c === '*') re += '.*';
    else if (c === '?') re += '.';
    else re += c.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${re}$`, 'i');
}

/** Does one pattern (negation stripped by the caller) match this host? */
export function matchSshHostPattern(pattern: string, host: string): boolean {
  return hostPatternToRegExp(pattern).test(host);
}

/**
 * ssh's own list semantics, which are NOT "any of them matches":
 *
 *   a negated pattern that matches WINS, whatever else matched.
 *
 * So `['*.internal', '!db-legacy.internal']` offers the key for the estate and
 * withholds it from the one host that must not receive it — a subtraction there is
 * no other way to express, and the reason the dialect keeps `!` at all.
 * An empty list matches nothing: a key registered without a pattern reaches no host,
 * rather than every one of them.
 */
export function sshHostPatternsMatch(patterns: readonly string[], host: string): boolean {
  let matched = false;
  for (const raw of patterns) {
    const negated = raw.startsWith('!');
    const pattern = negated ? raw.slice(1) : raw;
    if (!matchSshHostPattern(pattern, host)) continue;
    if (negated) return false;
    matched = true;
  }
  return matched;
}

// ─── pinned host keys ────────────────────────────────────────────────────────

/**
 * The host key types a pinned entry may name — OpenSSH's current set. A type we do
 * not know is refused at registration rather than written into a `known_hosts` file
 * the box's `ssh` would then reject with a message nobody reads.
 */
export const SSH_HOST_KEY_TYPES = [
  'ssh-ed25519',
  'ecdsa-sha2-nistp256',
  'ecdsa-sha2-nistp384',
  'ecdsa-sha2-nistp521',
  'ssh-rsa',
  'rsa-sha2-256',
  'rsa-sha2-512',
  'sk-ssh-ed25519@openssh.com',
  'sk-ecdsa-sha2-nistp256@openssh.com',
] as const;
export const SshHostKeyTypeSchema = z.enum(SSH_HOST_KEY_TYPES);
export type SshHostKeyType = z.infer<typeof SshHostKeyTypeSchema>;

/**
 * ONE pinned host key, captured at REGISTRATION — the customer pastes it, or the
 * dashboard runs `ssh-keyscan` and shows it for confirmation.
 *
 * Stored STRUCTURED rather than as a `known_hosts` line, because the platform has to
 * answer "is this host pinned?" before a run starts (an unpinned host is refused by
 * name) and a line is a string nobody can query. {@link knownHostsLine} renders it
 * back on the box; {@link knownHostsCoverHost} answers the question.
 *
 * A HASHED entry (`|1|…`, `ssh-keyscan -H`) is unrepresentable here on purpose: the
 * hash is exactly the thing that stops us from checking a declared host is covered,
 * and a pin nobody can verify is the TOFU this feature refuses, one layer down.
 */
export const SshKnownHostEntrySchema = z.object({
  /** The host this key belongs to — matched against the declared host verbatim. */
  host: SshHostSchema,
  /** Non-standard port; the rendered line then uses ssh's `[host]:port` form. */
  port: z.int().min(1).max(65535).optional(),
  key_type: SshHostKeyTypeSchema,
  /** Base64 of the host key blob — the third field of an `ssh-keyscan` line. */
  key_base64: z
    .string()
    .min(1)
    .max(8192)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'the host key is base64, exactly as ssh-keyscan prints it'),
});
export type SshKnownHostEntry = z.infer<typeof SshKnownHostEntrySchema>;

/** Render one pinned entry as the `known_hosts` line the box writes. */
export function knownHostsLine(entry: SshKnownHostEntry): string {
  const host = entry.port === undefined ? entry.host : `[${entry.host}]:${entry.port}`;
  return `${host} ${entry.key_type} ${entry.key_base64}`;
}

/**
 * Is this host pinned by at least one of these entries? Host comparison is
 * case-insensitive (ssh's own), and a pattern is NOT accepted here: the pin names a
 * concrete host, so a key whose patterns reach a host it never pinned resolves to the
 * `unpinned_host` refusal instead of to a run that would decide trust on first
 * contact.
 */
export function knownHostsCoverHost(entries: readonly SshKnownHostEntry[], host: string): boolean {
  const target = host.toLowerCase();
  return entries.some((e) => e.host.toLowerCase() === target);
}

// ─── the key itself ──────────────────────────────────────────────────────────

/** Account-unique handle for a key — what a picker shows and what a refusal names. */
export const SshKeyNameSchema = z
  .string()
  .min(1)
  .max(SSH_KEY_NAME_MAX)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9 ._-]*$/,
    'a name holds letters, digits, space, dot, dash, underscore',
  );

/**
 * The OpenSSH public key line — `<type> <base64> [comment]`. Public by definition:
 * this one is stored in plaintext, returned by the API and shown in the dashboard.
 * The PRIVATE half is accepted exactly once, at registration, sealed immediately,
 * and never read back out of the store by any API path.
 */
export const SshPublicKeySchema = z
  .string()
  .min(1)
  .max(16384)
  .regex(
    /^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com) [A-Za-z0-9+/]+={0,2}( .*)?$/,
    'an OpenSSH public key line: `<type> <base64> [comment]`',
  );

/**
 * `SHA256:<43 base64url chars>` — `ssh-keygen -lf`'s fingerprint, the ONE identifier
 * a `POST` answers with. It is derived from the PUBLIC half, so publishing it leaks
 * nothing, and it is what lets a customer check that the key we hold is the key they
 * meant without us ever handing anything back.
 */
export const SshFingerprintSchema = z
  .string()
  .regex(/^SHA256:[A-Za-z0-9+/]{43}$/, 'a SHA256 OpenSSH fingerprint');

/**
 * The metadata half of a registered key — everything a read may return. The sealed
 * private key has no field here BY CONSTRUCTION, which is the same trick
 * `RepoSecretMeta` plays: a route that can only see this type cannot leak the value
 * even by mistake.
 */
export const SshKeyMetaSchema = z.object({
  id: z.uuid(),
  /** NULL ⇒ the account's ORG pool; set ⇒ that member's PERSONAL key. */
  user_id: z.uuid().nullable(),
  name: SshKeyNameSchema,
  public_key: SshPublicKeySchema,
  fingerprint: SshFingerprintSchema,
  host_patterns: z.array(SshHostPatternSchema).max(SSH_HOST_PATTERNS_MAX),
  known_hosts: z.array(SshKnownHostEntrySchema).max(SSH_KNOWN_HOSTS_MAX),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type SshKeyMeta = z.infer<typeof SshKeyMetaSchema>;

/**
 * The PUBLIC half of an `ssh_key` vault entry as it rides the fleet wire beside the
 * sealed blob ({@link SealedVaultEntrySchema} in `project-ingress.ts`). The box needs
 * all of it to write the run's `ssh_config`: the file name, which hosts the identity
 * is offered for, and the pinned host keys the run's `UserKnownHostsFile` is built
 * from. None of it is secret; all of it is decided by the platform before the agent
 * starts, so the box never learns a host or a pin from anything the model wrote.
 */
export const SshKeyWireMetaSchema = z.object({
  name: SshKeyNameSchema,
  fingerprint: SshFingerprintSchema,
  host_patterns: z.array(SshHostPatternSchema).min(1).max(SSH_HOST_PATTERNS_MAX),
  known_hosts: z.array(SshKnownHostEntrySchema).min(1).max(SSH_KNOWN_HOSTS_MAX),
});
export type SshKeyWireMeta = z.infer<typeof SshKeyWireMetaSchema>;

// ─── resolution: which key reaches a declared host ───────────────────────────

/** What resolution needs to know about a key. The sealed bytes are the caller's. */
export type SshKeyCandidate = {
  id: string;
  name: string;
  /** NULL ⇒ org pool; set ⇒ personal to that member. */
  userId: string | null;
  hostPatterns: readonly string[];
  knownHosts: readonly SshKnownHostEntry[];
};

export const SshHostRefusal = {
  /** No visible key claims this host — the repo declared a host nobody registered for. */
  NoKey: 'no_key',
  /** A key claims it, but nobody pinned its host key. First contact is when a MITM matters. */
  Unpinned: 'unpinned_host',
} as const;
export type SshHostRefusal = (typeof SshHostRefusal)[keyof typeof SshHostRefusal];

/**
 * The sentence a person reads. Both name the HOST, because the host is the thing the
 * operator has to go and fix — in `.maintainer.yml` for the first, in the key's
 * registration for the second.
 */
export const SSH_HOST_REFUSAL_DETAIL: Record<SshHostRefusal, (host: string) => string> = {
  [SshHostRefusal.NoKey]: (host) =>
    `no SSH key is registered for host "${host}" — register one whose host_patterns match it, or remove the host from .maintainer.yml`,
  [SshHostRefusal.Unpinned]: (host) =>
    `host "${host}" has no pinned host key — pin it when registering the SSH key (ssh-keyscan) so the run can verify it; developerz never accepts an unknown host key`,
};

export type ResolveSshKeysInput = {
  /** Every key the ACCOUNT holds — org pool and personal keys of any member. */
  keys: readonly SshKeyCandidate[];
  /** The concrete host the repo declared. Never a pattern, never model-authored. */
  host: string;
  /**
   * The member the run acts for. A personal key is visible ONLY to its owner; with
   * no acting user (autonomous org work) only org keys are visible. Same fence as
   * `visibleToActor` in `llm-credential.ts` — a personal credential never quietly
   * signs somebody else's run.
   */
  actingUserId?: string | undefined;
};

export type ResolveSshKeysResult =
  | { ok: true; order: SshKeyCandidate[] }
  | { ok: false; refusal: SshHostRefusal; detail: string };

/**
 * Resolve the account's SSH keys for ONE declared host. `order[0]` is the key the run
 * uses; the rest is the deterministic remainder (a host reachable by two keys is a
 * real configuration, and the run must not pick differently on a re-list).
 *
 * Ranking: **personal before org** — the same tie-break `resolveLlmFromPool` applies,
 * for the same reason: a member's own credential is the narrower authority, so it is
 * the one their run should spend. Within a tier, name then id, which is a total order
 * over the data rather than over row order.
 *
 * A key must clear BOTH gates to be offered: its patterns match the host, and it
 * pinned that host's key. Splitting them is what produces the two refusals — a host
 * nobody registered for reads differently from a host somebody registered for and
 * never pinned, and an operator needs to know which mistake they made.
 */
export function resolveSshKeysForHost(input: ResolveSshKeysInput): ResolveSshKeysResult {
  const visible = input.keys.filter(
    (k) =>
      k.userId === null || (input.actingUserId !== undefined && k.userId === input.actingUserId),
  );
  const claiming = visible.filter((k) => sshHostPatternsMatch(k.hostPatterns, input.host));
  if (claiming.length === 0) {
    return {
      ok: false,
      refusal: SshHostRefusal.NoKey,
      detail: SSH_HOST_REFUSAL_DETAIL[SshHostRefusal.NoKey](input.host),
    };
  }

  const pinned = claiming.filter((k) => knownHostsCoverHost(k.knownHosts, input.host));
  if (pinned.length === 0) {
    return {
      ok: false,
      refusal: SshHostRefusal.Unpinned,
      detail: SSH_HOST_REFUSAL_DETAIL[SshHostRefusal.Unpinned](input.host),
    };
  }

  const order = [...pinned].sort((a, b) => {
    const personal = Number(b.userId !== null) - Number(a.userId !== null);
    if (personal !== 0) return personal;
    return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  });
  return { ok: true, order };
}
